<template>
    <div class="card">
        <div class="d-flex card-header justify-content-between align-items-center">
            <div>
                <div class="title">{{ $t('enps.enps') }}</div>
                <p class="description">
                    <span>{{ $t('enps.description') }}</span>
                </p>
            </div>
            <div>
                <b-button
                    id="btn-enps-new-research"
                    class="button-new-research"
                    size="lg"
                    variant="primary"
                    @click="onClickCreateResearch"
                >
                    <div class="create-new-research">
                        {{ $t('enps.createNewResearch') }}
                    </div>
                </b-button>
            </div>
        </div>
        <div class="card-body">
            <div v-if="itemsIsEmpty">
                <p class="label-no-data">{{ $t('enps.phraseEmptyScreen') }}</p>
                <img class="center-img" src="@/assets/images/icon-enps.svg" />
            </div>
            <b-table v-else :busy="isLoading" :fields="fields" :items="items" borderless>
                <template #table-busy>
                    <div class="text-center my-2">
                        <b-spinner class="align-middle" />
                    </div>
                </template>

                <template #cell(title)="{ item }">
                    <span class="mr-2">{{ item.title }}</span>
                    <a
                        v-if="!item.is_expired && item.applied"
                        id="btn-enps-see-research-results-icon"
                        class="icon-button"
                        @click="viewResults(item.id)"
                    >
                        <i class="fas fa-chart-line" />
                    </a>
                </template>

                <template #cell(expired_date)="row">
                    <div>{{ row.item.expired_date | formatDate }}</div>
                </template>

                <template #cell(total_responses)="{ item }">
                    <b-progress :max="item.progress.total">
                        <b-progress-bar :value="item.progress.total_answered">
                            <div class="progress-bar-info">
                                {{ item.progress.total_answered }} /
                                {{ item.progress.total }}
                            </div>
                        </b-progress-bar>
                    </b-progress>
                </template>

                <template #cell(actions)="row">
                    <div class="d-flex float-right">
                        <div v-if="!row.item.is_expired">
                            <div v-if="!row.item.applied">
                                <b-button
                                    id="btn-enps-apply-reasearch"
                                    size="sm"
                                    variant="primary"
                                    @click="onClickApplyResearch(row.item.id, row.item.expired_date)"
                                    >{{ $t('enps.apply') }}</b-button
                                >
                            </div>
                            <div v-else>
                                <b-button
                                    id="btn-enps-reapply-reasearch"
                                    class="mr-3 button-reapply"
                                    size="sm"
                                    @click="onClickApplyResearch(row.item.id, row.item.expired_date)"
                                    >{{ $t('enps.reapply') }}</b-button
                                >
                                <b-button
                                    id="btn-enps-close-reasearch"
                                    class="button-close"
                                    size="sm"
                                    @click="onClickCloseResearch(row.item.id)"
                                    >{{ $t('enps.close') }}</b-button
                                >
                            </div>
                        </div>
                        <div v-if="row.item.is_expired">
                            <b-button size="sm" variant="primary" class="m-0" @click="viewResults(row.item.id)">{{
                                $t('enps.viewResults')
                            }}</b-button>
                        </div>
                        <b-dropdown dropleft no-caret size="sm" variant="white" class="ml-2">
                            <template #button-content>
                                <i class="fas fa-ellipsis-v text-muted" />
                            </template>
                            <b-dropdown-item id="btn-enps-edit-research" @click="onClickEditResearch(row.item.id)">{{
                                $t('form.button.edit')
                            }}</b-dropdown-item>
                            <b-dropdown-divider />
                            <b-dropdown-item
                                id="btn-enps-duplicate-research"
                                @click="onClickDuplicateResearch(row.item.id)"
                                >{{ $t('form.button.duplicate') }}</b-dropdown-item
                            >
                            <b-dropdown-divider />
                            <b-dropdown-item id="btn-enps-delete-research" @click="deleteEnpsResearch(row.item.id)">{{
                                $t('form.button.delete')
                            }}</b-dropdown-item>
                        </b-dropdown>
                    </div>
                </template>
            </b-table>
        </div>
        <AppliedModal :expired_date="expiredDate" />
        <router-view
            @research-created="getEnpsResearches"
            @research-updated="getEnpsResearches"
            @update-expired-date="setExpiredDate"
        />
    </div>
</template>

<script>
import i18n from '@/i18n'
import { AppliedModal } from './children'
import { proceedWithConfirmation } from '@/lib/sweet-alert'
import { notificationSuccessfully, notificationError } from './functions'
import enpsMixin from './children/enpsMixin'
import { getDateWithTime } from '@/lib/date'
import { deleteEnpsResearch, getEnpsResearches, closeResearch } from './api'

export default {
    name: 'Enps',
    components: {
        AppliedModal,
    },
    mixins: [enpsMixin],
    data() {
        return {
            items: [],
            isLoading: false,
            expiredDate: null,
            fields: [
                { key: 'title', label: i18n.t('enps.form.researchName') },
                {
                    key: 'expired_date',
                    label: i18n.t('enps.endsIn'),
                    class: 'small-column text-center',
                },
                {
                    key: 'total_responses',
                    label: i18n.t('enps.totalResponses'),
                    class: 'progress-bar-column text-center',
                },
                {
                    key: 'actions',
                    label: i18n.t('people.actions'),
                    class: 'small-column text-center',
                },
            ],
        }
    },
    mounted() {
        this.getEnpsResearches()
    },
    computed: {
        itemsIsEmpty() {
            return this.items.length === 0 && this.isLoading == false
        },
    },
    methods: {
        setExpiredDate(expiredDate) {
            this.expiredDate = expiredDate
        },
        onClickApplyResearch(id, expiredDate) {
            proceedWithConfirmation({
                title: i18n.t('enps.modal.applyConfirmationTitle'),
                message: i18n.t('enps.modal.applyConfirmationText'),
                confirmButtonText: i18n.t('enps.apply'),
                cancelButtonText: i18n.t('enps.cancel'),
                onConfirm: () => {
                    return this.onApplyResearch(id)
                },
                callback: () => {
                    this.setExpiredDate(expiredDate)
                    this.$bvModal.show('notificationSuccessfully')
                    this.getEnpsResearches()
                },
                onError: () => notificationError(),
            })
        },
        onClickCloseResearch(id) {
            proceedWithConfirmation({
                onConfirm: () => closeResearch(id),
                callback: () => {
                    notificationSuccessfully('close')
                    this.getEnpsResearches()
                },
                onError: () => notificationError(),
            })
        },
        onClickEditResearch(enps_id) {
            this.$router.push(`/v2/administrator/enps/edit/${enps_id}`)
        },
        onClickDuplicateResearch(enps_id) {
            this.$router.push(`/v2/administrator/enps/duplicate/${enps_id}`)
        },
        onClickCreateResearch() {
            this.$router.push('/v2/administrator/enps/new')
        },
        viewResults(researchId) {
            this.$router.push({
                path: '/v2/reports/enps',
                query: { researchId },
            })
        },
        getEnpsResearches() {
            this.isLoading = true
            return getEnpsResearches({ with: 'progress' })
                .then((researches) => {
                    this.items = this.parseResearches(researches)
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        parseResearches(items) {
            return items.map((item) => {
                const progress = item.progress
                return {
                    ...item,
                    progress: {
                        total: progress ? parseFloat(progress.total) : 0,
                        total_answered: progress ? parseFloat(progress.total_answered) : 0,
                    },
                }
            })
        },
        deleteEnpsResearch(id) {
            proceedWithConfirmation({
                onConfirm: () => {
                    return deleteEnpsResearch(id).then(() => {
                        this.remoteResearch(id)
                    })
                },
                callback: () => notificationSuccessfully('delete'),
                onError: () => notificationError(),
            })
        },
        remoteResearch(id) {
            this.items = this.items.filter((item) => item.id != id)
        },
    },
    filters: {
        formatDate: function (value) {
            return getDateWithTime(value, 'DD/MM/YYYY HH:mm:ss')
        },
    },
}
</script>

<style scoped lang="scss">
.card {
    background-color: #ffffff;
}
.card-header {
    padding: 0.75rem 1.25rem;
    margin-bottom: 0;
    background-color: #f9f9f9;
    border-bottom: 1px solid #bec0c0;
}
.title {
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px;
    letter-spacing: 0;
    text-align: left;
    color: #022b4f;
}
.description {
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0.002em;
    text-align: left;
    color: #3b3f4e;
}
.button-new-research {
    width: 207px;
    height: 35px;
    background: #009ada;
}
.create-new-research {
    font-weight: bold;
    font-size: 16px;
    line-height: 19px;
    text-align: center;
    color: #ffffff;
}
.button-reapply {
    background: white;
    border-color: #6e6e6e;
    color: #6e6e6e;
}

.button-close {
    background: #6e6e6e;
    color: white;
}

.label-no-data {
    font-family: Roboto;
    font-size: 24px;
    font-style: normal;
    font-weight: bold;
    line-height: 28px;
    text-align: center;
    color: #3b3f4e;
}

.center-img {
    display: block;
    margin-top: 48px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 48px;
}

.icon-button {
    cursor: pointer;
}

/deep/ .small-column {
    width: 20rem;
}

/deep/ .progress-bar {
    color: #ffffff;
    background-color: #022b4f;
}

/deep/ .progress {
    height: 28px;
    width: 15rem;
    background-color: #909090;
    border-radius: 50px;
}

/deep/ .progress-bar-info {
    text-align: center;
    padding: 10px;
}

/deep/ .progress-bar-column {
    width: 160px;
}

/deep/ thead {
    background: #f9f9f9;
    color: #6e6e6e;
    th:first-child {
        border-top-left-radius: 5px;
        border-bottom-left-radius: 5px;
    }
    th:last-child {
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;
    }
}
</style>
